import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { IMAGES, LocalizedContent } from '../data/siteContent';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { Navigation } from './Navigation';
import { TravelConcierge } from './concierge/TravelConcierge';
import { CinematicAtmosphere } from './scenes/CinematicAtmosphere';
import { JourneyChapter } from './scenes/JourneyChapter';
import { SceneLayer } from './scenes/SceneLayer';
import { JourneyProgress } from './ui/JourneyProgress';
import { WhatsAppFloat } from './ui/WhatsAppFloat';
type Props = {
  content: LocalizedContent;
};
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
const SCENE_DURATION = 2.5;
const TRANSITION_OFFSET = 1.88;
export function CinematicJourney({
  content
}: Props) {
  const reducedMotion = useReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<ScrollTrigger | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeScene, setActiveScene] = useState(0);
  const sceneCount = content.chapters.length;
  useEffect(() => {
    document.documentElement.lang = content.htmlLang;
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [content.htmlLang]);
  const navigateToScene = (scene: number) => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const safeScene = Math.max(0, Math.min(scene, sceneCount - 1));
    const ratio = sceneCount > 1 ? safeScene / (sceneCount - 1) : 0;
    gsap.to(window, {
      duration: reducedMotion ? 0 : 1.05,
      ease: 'power3.inOut',
      overwrite: true,
      scrollTo: {
        y: trigger.start + (trigger.end - trigger.start) * ratio,
        autoKill: true
      }
    });
  };
  useLayoutEffect(() => {
    if (!wrapRef.current || !pinRef.current) return;
    const context = gsap.context(() => {
      const scenes = gsap.utils.toArray<HTMLElement>('.journey-scene');
      const veil = pinRef.current?.querySelector<HTMLElement>('.journey-veil');
      gsap.set(scenes, {
        autoAlpha: 0
      });
      gsap.set('.scene-copy', {
        autoAlpha: 0,
        y: reducedMotion ? 0 : 26
      });
      gsap.set('.scene-camera', {
        scale: 1
      });
      gsap.set('.atmosphere-mist--far', {
        xPercent: -4,
        yPercent: 2
      });
      gsap.set('.atmosphere-mist--near', {
        xPercent: 5,
        yPercent: -2
      });
      const timeline = gsap.timeline({
        defaults: {
          ease: 'none'
        },
        scrollTrigger: {
          trigger: wrapRef.current,
          start: 'top top',
          end: () => `+=${window.innerHeight * (reducedMotion ? sceneCount * 0.82 : sceneCount * 1.2)}`,
          pin: pinRef.current,
          scrub: reducedMotion ? 0.15 : 1.15,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: self => {
            setProgress(self.progress);
            const nextScene = Math.min(sceneCount - 1, Math.floor(self.progress * sceneCount));
            setActiveScene(current => current === nextScene ? current : nextScene);
          }
        }
      });
      triggerRef.current = timeline.scrollTrigger ?? null;
      scenes.forEach((scene, index) => {
        const at = index * TRANSITION_OFFSET;
        const camera = scene.querySelector<HTMLElement>('.scene-camera');
        const image = scene.querySelector<HTMLElement>('.scene-image');
        const copy = scene.querySelector<HTMLElement>('.scene-copy');
        const farMist = scene.querySelector<HTMLElement>('.atmosphere-mist--far');
        const nearMist = scene.querySelector<HTMLElement>('.atmosphere-mist--near');
        const enteringFromRight = index % 2 === 1;
        const cameraX = enteringFromRight ? 3.5 : -3.5;
        timeline.set(scene, {
          autoAlpha: 1
        }, at);
        if (camera) {
          timeline.fromTo(camera, {
            scale: reducedMotion ? 1.04 : 1.18,
            xPercent: reducedMotion ? 0 : cameraX
          }, {
            scale: 1.025,
            xPercent: 0,
            duration: SCENE_DURATION
          }, at);
        }
        if (image && !reducedMotion) {
          timeline.fromTo(image, {
            scale: 1.075,
            xPercent: enteringFromRight ? -1.8 : 1.8
          }, {
            scale: 1.01,
            xPercent: 0,
            duration: SCENE_DURATION
          }, at);
        }
        if (farMist && !reducedMotion) {
          timeline.to(farMist, {
            xPercent: enteringFromRight ? 5 : -5,
            yPercent: -2,
            duration: SCENE_DURATION
          }, at);
        }
        if (nearMist && !reducedMotion) {
          timeline.to(nearMist, {
            xPercent: enteringFromRight ? -7 : 7,
            yPercent: 3,
            duration: SCENE_DURATION
          }, at);
        }
        if (copy) {
          timeline.to(copy, {
            autoAlpha: 1,
            y: 0,
            duration: 0.42,
            ease: 'power2.out'
          }, at + 0.38);
          timeline.to(copy, {
            autoAlpha: 0,
            y: reducedMotion ? 0 : -16,
            duration: 0.32,
            ease: 'power2.in'
          }, at + 1.55);
        }
        if (index < scenes.length - 1) {
          if (veil) {
            timeline.to(veil, {
              autoAlpha: 0.9,
              scale: 1,
              duration: 0.24,
              ease: 'power2.in'
            }, at + 1.63);
            timeline.to(veil, {
              autoAlpha: 0,
              scale: 1.15,
              duration: 0.38,
              ease: 'power2.out'
            }, at + 1.87);
          }
          timeline.to(scene, {
            autoAlpha: 0,
            duration: 0.46,
            ease: 'power1.inOut'
          }, at + 1.98);
        }
      });
    }, wrapRef);
    return () => {
      triggerRef.current = null;
      context.revert();
    };
  }, [reducedMotion, sceneCount]);
  return <div className="relative w-full bg-[var(--ms-forest-deep)]" data-id="element-8" data-mp="2yr0v1z6ydtar">
      <Navigation content={content} onNavigate={navigateToScene} data-id="element-9" data-mp="2yqr5gcptlo5l" />
      <JourneyProgress content={content} progress={progress} activeScene={activeScene} sceneCount={sceneCount} onNavigate={navigateToScene} data-id="element-10" data-mp="3devppl4iu7qh" />
      <TravelConcierge content={content} data-id="element-11" data-mp="38ix76e5vjjxz" />
      <WhatsAppFloat content={content} data-id="element-12" data-mp="3mg2a45aak2p0" />

      <div ref={wrapRef} className="relative w-full" data-id="element-13" data-mp="3mgv7wgr9vhv0">
        <div ref={pinRef} className="relative h-[100svh] w-full overflow-hidden bg-[var(--ms-forest-deep)]" data-id="element-14" data-mp="3hi0p3etb1taw">
          {content.chapters.map((chapter, index) => <section key={chapter.id} className="journey-scene absolute inset-0" aria-label={chapter.mapLabel} data-id="element-15" data-mp="2f29lxq1j649s" data-mpi={index}>
              <SceneLayer src={chapter.image} alt={chapter.imageAlt} overlay={chapter.id === 'contact' ? 'full' : 'bottom'} data-id="element-16" data-mp="2o4spkcd54kx3" />
              <CinematicAtmosphere scene={chapter.atmosphere} data-id="element-17" data-mp="2sxc3xbjnjk2y" />
              <img src={IMAGES.watermarkLogo} alt="" aria-hidden="true" loading="eager" decoding="async" className={`journey-brand-watermark journey-brand-watermark--${index % 2 === 0 ? 'right' : 'left'}`} data-id="element-18" data-mp="2xu2gvq05tzk5" />
              <JourneyChapter chapter={chapter} content={content} isOpening={index === 0} onAdvance={() => navigateToScene(index + 1)} data-id="element-19" data-mp="3bs96yjyvav99" />
            </section>)}
          <div className="journey-veil pointer-events-none absolute inset-0 z-20" aria-hidden="true" data-id="element-20" data-mp="1hh3vithjhejd" />
        </div>
      </div>
    </div>;
}